import { call, put, takeEvery, all } from 'redux-saga/effects';
import { tableConfig } from 'src/settings';
import { ICollectionResponse, IRequestParams, IResponse } from '@dash/apiInstance';
import { IActions } from '@dash/actions';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import Api, { executeAction } from '../../../helpers/api';
import actions from './actions';
import actionTypes from './actionTypes';
import { IJob, ILoadTimesheetsPathParams, IProcessActionResponse, ITimesheet } from './types';
import notifications from '../../../redux/notifications/actions';
import { LoadTimesheetItemRequestAction, LoadTimesheetsRequestAction, ProcessActionRequestAction } from './actions.d';
import { t } from '../../../i18n';

const api = new Api('jobs/timesheets/:id');
const itemApi = new Api('jobs/:id/contested_hours');

function* loadTimesheets({ payload }: LoadTimesheetsRequestAction<ILoadTimesheetsPathParams, {}>) {
  try {
    const { page = 1, pageSize = tableConfig.pageSize, column, sort, order, filter = '', type } = payload;

    const result: ICollectionResponse<ITimesheet> = yield call<
      any,
      IRequestParams<ILoadTimesheetsPathParams, {}>,
      string
    >(
      sendRequest,
      api.load,
      {
        search: { page, pageSize, column, sort, order, filter },
        id: type,
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      yield put(
        actions.loadTimesheetsSuccess({
          timesheets: result.data?.success?.data?.records,
          total: result.data?.success?.data?.total,
          filteredTotal: result.data?.success?.data?.filteredCount,
          totalCount: result.data?.success?.data?.totalCount,
          sortableColumns: result.data?.success?.data?.sortableColumns,
          bulkActions: result.data?.success?.data?.bulk_actions as unknown as IActions,
          filterApplied: !!filter,
          type,
        }),
      );
    } else {
      yield put(actions.loadTimesheetsError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadTimesheetItem({ payload }: LoadTimesheetItemRequestAction<ILoadTimesheetsPathParams, {}>) {
  try {
    const { page = 1, pageSize = tableConfig.pageSize, column, order, filter = '', id } = payload;

    const result: ICollectionResponse<ITimesheet> = yield call<
      any,
      IRequestParams<ILoadTimesheetsPathParams, {}>,
      string
    >(
      sendRequest,
      itemApi.load,
      { search: { page, pageSize, column, order, filter }, id },
      notifications.REQUEST_ERROR,
    );

    if (result?.status === 200) {
      yield put(
        actions.loadTimesheetItemSuccess({
          id,
          job: result.data?.success?.data?.job as unknown as IJob,
        }),
      );
    } else {
      yield put(actions.loadTimesheetsError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* processAction({ payload }: ProcessActionRequestAction<{}, {}>) {
  try {
    const { url, method, data, key, type } = payload;

    const result: IResponse<IProcessActionResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      executeAction,
      { url, method, data },
      notifications.REQUEST_ERROR,
    );

    const isBulkAction = ['bulk_approve', 'bulk_deny'].includes(key);

    if (result?.messages || result?.success) {
      const job = result?.data?.job;

      if (isBulkAction) {
        const jobs = result?.data?.jobs || [];
        yield all(
          jobs.map((bulkJob: any) =>
            bulkJob?.status?.id === 'resolved'
              ? put(actions.moveTimesheetToResolved({ job: bulkJob, sourceType: type }))
              : put(actions.processActionSuccess({ job: bulkJob as IJob })),
          ),
        );
      } else {
        const isResolved = job?.status?.id === 'resolved';

        if (isResolved) {
          yield put(actions.moveTimesheetToResolved({ job, sourceType: type }));
        } else {
          yield put(actions.processActionSuccess({ job: job as IJob }));
        }
      }

      if (key === 'edit') {
        yield put({ type: actionTypes.CHANGE_EDIT_HOURS_MODAL_VISIBILITY });
      }

      if (isBulkAction) {
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: result?.messages?.join(' ') || '',
          }),
        );
      } else {
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t(`dash.jobs.openTimesheetsList.messages.${key}_success_message`),
          }),
        );
      }
    } else {
      yield put(actions.processActionError());
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actionTypes.LOAD_TIMESHEETS_REQUEST, loadTimesheets)]);
  yield all([takeEvery(actionTypes.LOAD_TIMESHEET_ITEM_REQUEST, loadTimesheetItem)]);
  yield all([takeEvery(actionTypes.PROCESS_ACTION_REQUEST, processAction)]);
}
